import { useState } from "react";

const p = "text-sm md:text-base leading-relaxed text-[#002726]";
const h2 = "text-2xl font-medium text-[#002726]";
const h3 = "text-xl font-medium text-[#002726]";
const link = "text-blue-600 hover:underline";
/** Neutral disclaimer-style panel (no red/green) */
const disclaimer =
  "rounded-xl bg-[#F3F4F6] p-5 text-sm text-[#002726] md:text-base leading-relaxed";

const LAST_UPDATED_TEXT = "This policy was last updated on 10th April 2026.";

const FAQ_ITEMS = [
  {
    cat: "eligibility",
    q: "I signed up but the course style isn't what I expected. Can I get a refund?",
    a: "If you're within the 7-day window and haven't accessed any content, attended any sessions, or redeemed any tools, you're eligible under our standard policy. If you're outside the window, write to us - we'll look at the specifics and see if there's anything we can do.",
  },
  {
    cat: "eligibility",
    q: "The batch dates changed after I enrolled. Am I eligible?",
    a: "Schedule changes and postponements don't qualify for a refund under our policy. Your enrollment carries forward to the rescheduled batch. If the new dates are more than 21 days from the original, reach out to us and we'll discuss your options.",
  },
  {
    cat: "eligibility",
    q: "I've been enrolled but haven't started yet. Can I cancel?",
    a: "Not having started the course doesn't automatically qualify you for a refund - the 7-day window applies from the date of payment or enrollment, whichever is earlier. If you're still within that window and haven't accessed any content, check the eligibility criteria for your course type in Section 2.",
  },
  {
    cat: "eligibility",
    q: "I had a medical emergency during the program. What can I do?",
    a: "Write to us at hi@growthschool.io with a brief explanation and any relevant documentation. We review medical and bereavement situations with empathy and will try to find a fair resolution - including possible deferral or credit options. You have up to 7 days after the program concludes to raise such a claim.",
  },
  {
    cat: "eligibility",
    q: "Can I transfer my enrollment to someone else instead of getting a refund?",
    a: "Enrollments are non-transferable. If you're unable to attend, please reach out and we'll explore options like deferral to a future cohort where possible.",
  },
  {
    cat: "eligibility",
    q: "What if I'm not satisfied with the quality of the program?",
    a: "We take quality feedback seriously. If you feel the program didn't deliver on what was promised, document the specific gap and write to us during the active program period. Post-program claims are generally not eligible for a refund - but your feedback is genuinely used to improve future cohorts.",
  },
  {
    cat: "eligibility",
    q: "I completed the course but didn't find it useful. Can I get a refund?",
    a: "Once a program has been completed, refunds are not available regardless of the outcome. Learning results are closely tied to individual engagement, effort, and application - and completing the course indicates full access and participation. If you have specific feedback on quality, we'd genuinely like to hear it.",
  },
  {
    cat: "eligibility",
    q: "I signed up for a bundle. Can I get a refund for just one course in it?",
    a: "Bundles are treated as a single transaction. Refunds cannot be requested for individual courses within a bundle. If the entire bundle meets the eligibility criteria and none of the courses have been accessed, you may request a refund for the full bundle.",
  },
  {
    cat: "eligibility",
    q: "I was gifted or sponsored for a course. Does this policy still apply?",
    a: "Yes. Regardless of who made the payment, the standard refund policy applies to the enrolled participant. If the purchase was made under a corporate or enterprise sponsorship agreement, the terms of that agreement take precedence.",
  },
  {
    cat: "eligibility",
    q: "The instructor for my cohort changed. Can I get a refund?",
    a: "A change in instructor isn't something we're able to offer a refund for. GrowthSchool programs are designed around a curriculum and learning framework, not tied exclusively to a single instructor. We commit to maintaining the quality and objectives of the program regardless of facilitation changes.",
  },
  {
    cat: "eligibility",
    q: "I attended the orientation call. Does that count as a session?",
    a: "No. Introductory or onboarding calls held before the first substantive session are not counted as a live session for refund eligibility purposes. Attending an orientation call alone does not disqualify your refund request.",
  },
  {
    cat: "eligibility",
    q: "I want a refund but my 7-day window just passed by a day or two. Is there any flexibility?",
    a: "The 7-day window is a firm policy boundary and exists to ensure fairness across all learners. We're unable to make exceptions based on timing alone. If there are extraordinary circumstances behind the delay, write to us and we'll review it - but we cannot commit to flexibility on this.",
  },
  {
    cat: "eligibility",
    q: "I redeemed a tool but haven't used it. Does that affect my refund?",
    a: "Yes. Redemption - not usage - is the trigger. Once a tool or voucher is redeemed, its fair market value will be deducted from any approved refund, regardless of whether you actually used it. We'll communicate the deduction amount in writing before processing.",
  },
  {
    cat: "payments",
    q: "I paid twice by accident. What happens?",
    a: "Email us at hi@growthschool.io right away with both payment reference numbers. Duplicate payments are refunded in full, typically within 7 business days.",
  },
  {
    cat: "payments",
    q: "My payment was deducted but I wasn't enrolled. What happens?",
    a: "This can occasionally happen due to a payment gateway delay. Write to us at hi@growthschool.io within 48 hours of the transaction with your payment reference number. We'll verify with our payment provider and either confirm your enrollment or process a full refund within 5 business days.",
  },
  {
    cat: "payments",
    q: "I bought the course during a sale or with a coupon. Does the same policy apply?",
    a: "Yes. Purchases made during promotional periods, flash sales, or with discount codes are subject to the same refund policy unless explicitly stated otherwise at the time of purchase. The discounted price does not extend or alter the refund window.",
  },
  {
    cat: "payments",
    q: "How will I know what deductions apply to my refund before it's processed?",
    a: "Before we process any approved refund, we will send you a written breakdown of all applicable deductions for your review. The refund will only be processed after you've had the chance to see the final amount. No deductions are applied without prior communication.",
  },
  {
    cat: "payments",
    q: "I enrolled on behalf of someone else or my company. Can they get a refund?",
    a: "Individual purchases made on behalf of another person are still governed by this policy. If your company or organisation enrolled multiple participants under a group or enterprise agreement, the refund terms from that specific agreement apply - please refer to your contract or write to us directly.",
  },
  {
    cat: "payments",
    q: "My tool access isn't working. Does that affect my refund eligibility?",
    a: "Tool access issues should be reported to our support team immediately and we'll work to resolve them. Technical difficulties with tools are not automatic grounds for a refund, but we'll do our best to ensure you get what you enrolled for.",
  },
  {
    cat: "process",
    q: "I didn't receive a confirmation email after paying. What should I do?",
    a: "Check your spam or promotions folder first. If you still don't find it, write to us at hi@growthschool.io with your transaction ID and we'll verify your enrollment status and resend your confirmation within 24 hours.",
  },
  {
    cat: "process",
    q: "Can I pause my enrollment and resume later instead of cancelling?",
    a: "We don't currently offer a formal pause option for all programs. However, if you're facing a genuine situation that prevents you from continuing, write to us and we'll explore what's possible - including deferral to an upcoming batch in select cases.",
  },
  {
    cat: "process",
    q: "I requested a refund but changed my mind - can I withdraw the request?",
    a: "Yes. If your refund hasn't been processed yet, write to us at hi@growthschool.io and we'll cancel the request and retain your enrollment. Once a refund has been processed, re-enrollment would require a fresh purchase.",
  },
  {
    cat: "process",
    q: "Will a refund affect my access to the course immediately?",
    a: "Yes. Once a refund is initiated and approved, your access to the program - including content, live sessions, community platforms, and tools - will be revoked. Access cannot be retained after a refund is processed.",
  },
];

function FaqAnswer({ text }) {
  const parts = text.split(/(hi@growthschool\.io)/g);
  return (
    <p className={`${p} !mt-0`}>
      {parts.map((part, i) =>
        part === "hi@growthschool.io" ? (
          <a key={i} href="mailto:hi@growthschool.io" className={link}>
            hi@growthschool.io
          </a>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </p>
  );
}

export default function RefundPolicyBody() {
  const [openFaqs, setOpenFaqs] = useState(() => new Set());

  const toggleFaq = (id) => {
    setOpenFaqs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <article className="w-full">
      <div className="space-y-10">
        <header className="space-y-4">
          <p className="text-sm text-[#686C7A]">{LAST_UPDATED_TEXT}</p>
          <h1 className="text-3xl md:text-4xl font-normal text-[#002726] mb-3">
            Refund Policy
          </h1>
          <p className={p}>
            We build programs we&apos;re genuinely proud of - and we know
            enrolling takes real commitment on your part. This page explains
            exactly when you&apos;re eligible for a refund, what to expect, and
            how to request one. Written in plain language, no fine print
            surprises.
          </p>
        </header>

        {/* Quick reference */}
        <section className="space-y-4" aria-labelledby="quick-ref-heading">
          <h2 id="quick-ref-heading" className={h2}>
            Are You Eligible?
          </h2>
          <p className={p}>
            Find your situation below. Full conditions are detailed in the
            sections that follow.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="p-3 font-semibold text-[#686C7A]">Situation</th>
                  <th className="p-3 font-semibold text-[#686C7A]">Eligible?</th>
                  <th className="hidden p-3 font-semibold text-[#686C7A] md:table-cell">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  [
                    "Self-paced course - changed your mind early",
                    "✓ Yes",
                    "Within 7 days + no content accessed + no tools redeemed",
                    "#section-2-1",
                  ],
                  [
                    "Instructor-led course - changed your mind early",
                    "✓ Yes",
                    "Within 7 days + no sessions attended + no tools redeemed",
                    "#section-2-2",
                  ],
                  [
                    "GrowthSchool cancels the entire program",
                    "✓ Yes",
                    "100% refund, no conditions",
                    "#section-3-1",
                  ],
                  [
                    "Duplicate payment",
                    "✓ Yes",
                    "Full refund within 7 business days",
                    "#section-4-1",
                  ],
                  [
                    "Payment deducted but enrollment not confirmed",
                    "✓ Yes",
                    "Write to us within 48 hours with transaction ID",
                    "#section-4-2",
                  ],
                  [
                    "Any course content or resources accessed since enrollment",
                    "✕ No",
                    "Content access falls outside our standard refund policy",
                    "#section-5-2",
                  ],
                  [
                    "Tools or vouchers redeemed since enrollment",
                    "~ Partial",
                    "Refund may still apply - fair market value of redeemed tools deducted",
                    "#section-6-1",
                  ],
                  [
                    "Personal reasons - non-attendance, schedule conflicts, or change of mind after 7 days",
                    "✕ No",
                    "Personal circumstances don't qualify",
                    "#section-7",
                  ],
                  [
                    "Course completed",
                    "✕ No",
                    "Completion indicates full participation",
                    "#section-7",
                  ],
                ].map(([situation, badge, details, hash]) => (
                  <tr key={situation}>
                    <td className="p-3 text-[#002726]">{situation}</td>
                    <td className="p-3">
                      <span className="font-semibold text-[#002726]">
                        {badge}
                      </span>
                      <a href={hash} className={`${link} ml-2 md:hidden`}>
                        (link)
                      </a>
                    </td>
                    <td className="hidden p-3 text-[#002726]/90 md:table-cell">
                      {details}{" "}
                      <a href={hash} className={link}>
                        (see section)
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 1 */}
        <section
          id="section-1"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>General Payment Terms</h2>

          <div className="space-y-3">
            <h3 className={h3}>1.1 How Your Enrollment Works</h3>
            <p className={p}>
              When you enroll in a GrowthSchool program, your seat is confirmed
              immediately and we begin setting up your access - content,
              community, tools, and live sessions. Because we start delivering
              value from the moment you enroll, refunds are available only
              within the specific windows described in this policy.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>1.2 Non-Transferability</h3>
            <p className={p}>
              Enrollments are personal and non-transferable. You cannot transfer
              your seat, login access, or course entitlements to another
              individual, whether for free or for consideration. If you are
              unable to attend, your enrollment cannot be reassigned to a
              colleague, friend, or family member.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>1.3 Installment and Part-Payments</h3>
            <p className={p}>
              If you enrolled using a payment plan or installment option, all
              installments remain due regardless of attendance or usage - except
              in cases where GrowthSchool cancels the program entirely, in which
              case no further installments will be collected and amounts already
              paid will be refunded in full. Discontinuing payments in all other
              circumstances does not constitute a cancellation request and does
              not entitle you to a refund of amounts already paid. Failure to
              complete installments may result in suspension of access to the
              program.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>1.4 Currency and Taxes</h3>
            <p className={p}>
              All refunds will be processed in their original currency of
              payments. Tax treatment on refunds will be handled in accordance
              with applicable tax laws at the time of processing.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section
          id="section-2"
          className="space-y-8 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>Refund Eligibility by Course Type</h2>

          <div id="section-2-1" className="space-y-4 scroll-mt-24">
            <h3 className={h3}>2.1 Self-Paced Learning Courses</h3>
            <p className={p}>
              Self-paced courses give you the flexibility to learn on your own
              schedule. Because access to all content is set up and made
              available immediately upon enrollment, refund eligibility is
              time-bound and access-bound.
            </p>
            <div className="space-y-6">
              <div>
                <p className="mb-2 text-sm font-bold uppercase tracking-wide text-[#002726]">
                  Eligible if all apply
                </p>
                <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
                  <li>
                    Request submitted within <strong>7 calendar days</strong> of
                    payment or enrollment, whichever is earlier
                  </li>
                  <li>
                    You have <strong>not accessed</strong> any course content,
                    modules, or resources since enrollment
                  </li>
                  <li>
                    You have <strong>not redeemed</strong> any tools, vouchers,
                    coupons, or special resources
                  </li>
                  <li>
                    Your account is not under investigation for any policy
                    violation or misuse
                  </li>
                </ul>
              </div>
              <div>
                <p className="mb-2 text-sm font-bold uppercase tracking-wide text-[#002726]">
                  Not eligible if any apply
                </p>
                <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
                  <li>The 7-day window has passed</li>
                  <li>
                    You have accessed any course content, modules, or resources
                    since enrollment
                  </li>
                  <li>
                    You have redeemed any tools, software access, vouchers, or
                    partner resources
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div id="section-2-2" className="space-y-4 scroll-mt-24">
            <h3 className={h3}>2.2 Instructor-Led and Cohort-Based Programs</h3>
            <p className={p}>
              Instructor-led programs - including our flagship cohorts,
              masterminds, and accelerators - involve significant advance
              preparation, fixed session schedules, live facilitation, cohort
              coordination, and real-time resource allocation. Because of this,
              the refund window is strictly defined.
            </p>
            <div className="space-y-6">
              <div>
                <p className="mb-2 text-sm font-bold uppercase tracking-wide text-[#002726]">
                  Eligible if all apply
                </p>
                <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
                  <li>
                    Request submitted within <strong>7 calendar days</strong> of
                    payment or enrollment, whichever is earlier
                  </li>
                  <li>
                    You have <strong>not attended</strong> any live sessions in
                    the program
                  </li>
                  <li>
                    You have <strong>not redeemed</strong> any tools, vouchers,
                    coupons, or special resources
                  </li>
                  <li>Your account is not under investigation</li>
                </ul>
              </div>
              <div>
                <p className="mb-2 text-sm font-bold uppercase tracking-wide text-[#002726]">
                  Not eligible if any apply
                </p>
                <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
                  <li>
                    The 7-day window has passed, even if the program hasn&apos;t
                    started yet
                  </li>
                  <li>You have attended any live sessions in the program</li>
                  <li>
                    You have redeemed any tools, software access, vouchers, or
                    partner resources
                  </li>
                </ul>
              </div>
            </div>
            <p className={p}>
              <strong>Note on Orientation Sessions:</strong> Introductory or
              onboarding calls held prior to the first substantive session are
              not counted as a live session for refund purposes. Attending an
              orientation call alone does not disqualify your refund request.
            </p>
          </div>

          <div id="section-2-3" className="space-y-3">
            <h3 className={h3}>2.3 Workshop and Short-Format Programs</h3>
            <p className={p}>
              Single-day workshops, intensive bootcamps, and short-format
              programs of fewer than 3 sessions are{" "}
              <strong>non-refundable</strong> once the first session has
              commenced.
            </p>
            <p className={p}>
              For this course type, the standard 7-day-from-payment window
              applies first. After that window closes, and until 7 days before
              the event date, refund requests will be evaluated on a
              case-by-case basis. Requests submitted within 7 days of the event
              date are not eligible. Approved refunds for this category are
              subject to the standard deduction terms outlined in{" "}
              <a href="#section-5" className={link}>
                Section 5
              </a>
              .
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>
              2.4 Program Bundles and Multi-Course Packages
            </h3>
            <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
              <li>
                The bundle is treated as a single transaction for refund
                purposes
              </li>
              <li>
                Refunds are not available for individual courses within the
                bundle unless the entire bundle meets the eligibility criteria
              </li>
              <li>
                If any course in a bundle has been accessed, the entire bundle
                becomes ineligible for a refund
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>
              2.5 One-Time Offer (OTO) and Add-On Purchases
            </h3>
            <p className={p}>
              Any purchase made as an add-on or extension to an existing
              enrollment - including but not limited to program upgrades,
              session recordings, bonus modules, resource bundles, and program
              bumps - is treated as an extension of the original purchase
              transaction and is governed by the same refund terms applicable to
              that primary enrollment.
            </p>
            <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
              <li>
                Add-on purchases are non-refundable once the original program
                enrollment has crossed its refund eligibility threshold
              </li>
              <li>
                If the original enrollment is still within its eligible refund
                window, the add-on may be considered as part of a combined
                refund request - subject to the same access and redemption
                conditions
              </li>
              <li>
                OTO purchases made at the point of checkout are considered
                accepted and final upon payment, and are not eligible for
                standalone refund requests separate from the primary enrollment
              </li>
              <li>
                Recordings, bonus content, and downloadable resources are
                considered delivered upon access being granted, and are
                non-refundable once made available
              </li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section
          id="section-3"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>GrowthSchool-Initiated Changes</h2>

          <div id="section-3-1" className="space-y-3 scroll-mt-24">
            <h3 className={h3}>3.1 Full Program Cancellation by GrowthSchool</h3>
            <p className={p}>
              If GrowthSchool cancels an entire program without offering a
              rescheduled alternative, enrolled participants will receive a{" "}
              <strong>100% refund</strong> of the course fees paid. This is
              unconditional and will be processed within 7 to 10 business days
              of the cancellation announcement.
            </p>
            <div className={disclaimer}>
              This is the only scenario in which no deductions apply. A full
              refund is issued regardless of how much was paid or when you
              enrolled.
            </div>
          </div>

          <div id="section-3-2" className="space-y-3">
            <h3 className={h3}>3.2 Program Postponement or Rescheduling</h3>
            <p className={p}>
              GrowthSchool reserves the right to postpone, reschedule, or modify the
              timing of any program due to operational, logistical, or
              unforeseen circumstances. In these cases:
            </p>
            <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
              <li>
                No refund will be issued for a change in start date or schedule
              </li>
              <li>
                Participants retain full access to the rescheduled program under
                the same enrollment
              </li>
              <li>
                If the rescheduled date is more than <strong>21 days</strong>{" "}
                from the original date, participants may write to{" "}
                <a href="mailto:hi@growthschool.io" className={link}>
                  hi@growthschool.io
                </a>{" "}
                to discuss options - though a refund is not guaranteed in this
                scenario
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>3.3 Changes to Course Content or Structure</h3>
            <p className={p}>
              GrowthSchool continuously improves its programs based on learner
              feedback, industry developments, and curriculum updates. Changes
              to session structure, module sequencing, tools covered, or
              instructors do not constitute grounds for a refund. We commit to
              maintaining the core learning objectives and outcomes of every
              program as described at the time of enrollment.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>3.4 Technical Issues on GrowthSchool&apos;s End</h3>
            <p className={p}>
              If a live session is disrupted due to a technical failure on
              GrowthSchool&apos;s platform - and the session is not rescheduled or
              made available as a recording - affected participants will be
              entitled to a <strong>pro-rated credit</strong> toward a future
              session or program. This credit is non-transferable and valid for
              6 months from the date of issue.
            </p>
          </div>
        </section>

        {/* Section 4 */}
        <section
          id="section-4"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>Special Refund Scenarios</h2>

          <div id="section-4-1" className="space-y-3 scroll-mt-24">
            <h3 className={h3}>4.1 Duplicate Payment</h3>
            <p className={p}>
              If you have been charged more than once for the same program due
              to a payment processing error, the duplicate charge will be
              refunded in full. Email{" "}
              <a href="mailto:hi@growthschool.io" className={link}>
                hi@growthschool.io
              </a>{" "}
              with your payment reference numbers and registered email address.
              The refund will be processed within 7 business days via your
              original payment method.
            </p>
            <div className={disclaimer}>
              Attempting to initiate a duplicate payment refund for a
              transaction that was intentional - such as purchasing a second
              seat - will not be processed as a duplicate.
            </div>
          </div>

          <div id="section-4-2" className="space-y-3 scroll-mt-24">
            <h3 className={h3}>4.2 Payment Gateway or Bank Errors</h3>
            <p className={p}>
              If your payment was deducted but your enrollment was not confirmed
              due to a payment gateway failure, please write to{" "}
              <a href="mailto:hi@growthschool.io" className={link}>
                hi@growthschool.io
              </a>{" "}
              within 48 hours with your transaction ID. We will verify with our
              payment provider and either confirm your enrollment or initiate a
              full refund within 5 business days.
            </p>
          </div>

          <div id="section-4-3" className="space-y-3 scroll-mt-24">
            <h3 className={h3}>4.3 Medical or Bereavement Situations</h3>
            <p className={p}>
              We recognise that certain life circumstances are genuinely beyond
              your control. In the event of a serious medical emergency or
              bereavement affecting you directly or an immediate family member,
              you may write to us at{" "}
              <a href="mailto:hi@growthschool.io" className={link}>
                hi@growthschool.io
              </a>{" "}
              with a brief explanation and supporting documentation.
            </p>
            <p className={p}>
              These situations are reviewed with empathy on a case-by-case
              basis. While we cannot guarantee a refund outside the standard
              eligibility criteria, we will always try to find a fair resolution
              - which may include a program deferral, credit toward a future
              cohort, or a partial refund. These terms override the standard
              eligibility criteria in exceptional documented cases.
            </p>
            <div className={disclaimer}>
              Emergency claims must be raised within{" "}
              <strong>7 days of the program concluding</strong>. Retrospective
              claims submitted beyond this window will not be considered.
            </div>
          </div>

          <div id="section-4-4" className="space-y-3">
            <h3 className={h3}>4.4 Quality Concerns</h3>
            <p className={p}>
              We stand behind the quality of every program we build. If
              something fell short of what we promised, we want to know - and we
              want to make it right.
            </p>
            <p className={p}>
              If you have a specific concern about the quality of a program,
              please reach out to your program coordinator or write to us at{" "}
              <a href="mailto:hi@growthschool.io" className={link}>
                hi@growthschool.io
              </a>{" "}
              during the active program period. Tell us what fell short and how.
              We&apos;ll take it seriously and work toward a fair resolution -
              and if your concern warrants a refund review, our team will route
              it accordingly.
            </p>
            <p className={p}>
              Where a documented quality gap exists - one that is directly tied
              to what was stated at the time of enrollment - we will review your
              case in good faith. Outcomes that vary based on individual
              engagement, effort, or prior knowledge fall outside this scope, as
              these are factors we cannot control on your behalf.
            </p>
          </div>
        </section>

        {/* Section 5 */}
        <section
          id="section-5"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>Refund Deductions - What to Expect</h2>

          <div className="space-y-3">
            <h3 className={h3}>5.1 Why Deductions Exist</h3>
            <p className={p}>
              When you enroll in a GrowthSchool program, we incur real costs from
              the moment your payment is processed - platform setup, instructor
              scheduling, cohort coordination, community access, and third-party
              tool licensing, among others. Refund deductions exist to recover a
              portion of these costs fairly and transparently.
            </p>
          </div>

          <div id="section-5-2" className="space-y-3 scroll-mt-24">
            <h3 className={h3}>5.2 Deduction Example</h3>
            <div className={disclaimer}>
              You enrolled in a <strong>₹10,000 program</strong> and requested a
              refund within the eligible window without accessing any content or
              redeeming any tools. Only the payment processing fee applies (up
              to 5%).{" "}
              <strong>
                Your refund in this scenario would be a minimum of ₹9,500.
              </strong>
            </div>
            <p className={p}>
              The exact deductions applicable to your request will be
              communicated to you in writing before the refund is processed. You
              will have the opportunity to review these before they are applied.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>
              5.3 No Deductions for GrowthSchool-Initiated Cancellations
            </h3>
            <p className={p}>
              In the event that GrowthSchool cancels a program entirely as described
              in{" "}
              <a href="#section-3-1" className={link}>
                Section 3.1
              </a>
              , no deductions will be applied. A full 100% refund will be
              issued.
            </p>
          </div>
        </section>

        {/* Section 6 */}
        <section
          id="section-6"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>Tools, Vouchers, and Third-Party Resources</h2>

          <div id="section-6-1" className="space-y-3 scroll-mt-24">
            <h3 className={h3}>6.1 When You Redeem Tools or Vouchers</h3>
            <p className={p}>
              Many GrowthSchool programs include access to premium third-party
              tools, software subscriptions, partner vouchers, or exclusive
              resources as part of the learning experience. If any of these are
              redeemed - regardless of whether you have actively used them -
              their fair market value will be deducted from any approved refund
              amount.
            </p>
            <div className={disclaimer}>
              Redemption does not automatically disqualify your refund request,
              but the fair market value of redeemed tools or vouchers will be
              subtracted from the refund before it is issued. The applicable
              deduction will be communicated to you in writing before
              processing.
            </div>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>6.2 Availability of Tools and Coupons</h3>
            <p className={p}>
              Tools, software access, coupons, and partner vouchers included in
              any GrowthSchool program are offered as supplementary benefits to the
              core learning experience. Their inclusion is subject to the terms,
              pricing, licensing conditions, and continued availability of the
              respective third-party providers - all of which are outside
              GrowthSchool&apos;s control.
            </p>
            <p className={p}>
              GrowthSchool does not guarantee the continuous, uninterrupted, or
              indefinite availability of any specific tool, coupon, or partner
              resource at the time of your enrollment or thereafter. Specific
              tools and coupons advertised at the time of purchase are subject
              to change without prior notice, including but not limited to:
            </p>
            <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
              <li>
                Tools being discontinued, paused, or restricted by their
                respective providers
              </li>
              <li>
                Coupon codes expiring, reaching redemption limits, or being
                withdrawn by the issuing partner
              </li>
              <li>
                Changes in pricing tiers, access levels, or feature sets offered
                under the partnership
              </li>
              <li>
                Regional restrictions imposed by third-party providers that may
                limit access in certain geographies
              </li>
              <li>
                Replacement of a promised tool or coupon with an equivalent or
                alternative resource at GrowthSchool&apos;s discretion
              </li>
            </ul>
            <p className={p}>
              Where a tool or coupon becomes unavailable through no fault of the
              learner, GrowthSchool will make reasonable efforts to provide an
              equivalent alternative. However, the unavailability of a specific
              tool or coupon - whether before or after redemption - does not
              constitute grounds for a refund, partial or full. See{" "}
              <a href="#section-6-3" className={link}>
                Section 6.3
              </a>{" "}
              for the one exception.
            </p>
            <div className={disclaimer}>
              By enrolling in any GrowthSchool program, you acknowledge that tool
              and coupon availability is subject to third-party stipulations
              beyond our control, and that GrowthSchool&apos;s obligation is to make
              reasonable efforts to deliver the stated benefits - not to
              guarantee their availability in perpetuity.
            </div>
          </div>

          <div id="section-6-3" className="space-y-3 scroll-mt-24">
            <h3 className={h3}>6.3 Unredeemed Tools at Course End</h3>
            <p className={p}>
              If you have not redeemed tools or vouchers by the time your course
              or access period concludes, your access to those resources will
              expire. This does not entitle you to a refund, a credit, or any
              other compensation - except where the unavailability of tools was
              directly caused by GrowthSchool&apos;s failure to provision access
              within the program period, in which case the matter will be
              reviewed and resolved at our discretion. We encourage all learners
              to redeem available resources early to maximise the value of their
              enrollment.
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section
          id="section-7"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>Common Situations We&apos;re Unable to Refund</h2>
          <p className={p}>
            A few situations that fall outside our policy - we want to be
            upfront about these so there are no surprises on either side.
          </p>
          <ul className="list-disc space-y-2 pl-6 text-sm text-[#002726] md:text-base">
            {[
              ["Change of mind after 7 days", "The refund window is firm"],
              [
                "Any access to course content or resources",
                "This falls outside our standard policy for refunds",
              ],
              [
                "Non-attendance or low engagement",
                "Not showing up doesn't qualify you for a return",
              ],
              [
                "Dissatisfaction with outcomes",
                "Learning results depend heavily on individual effort",
              ],
              [
                "Personal scheduling conflicts",
                "Work, travel, or life changes after enrollment are not covered",
              ],
              [
                "Course content updates",
                "We continuously improve programs; changes are part of this",
              ],
              [
                "Enterprise or group buyers",
                "Governed by separate contractual agreements",
              ],
              [
                "Post-program requests",
                "Beyond the 7-day post-program emergency window",
              ],
              [
                "Coupon or flash sale purchases",
                "Final unless otherwise stated at time of purchase",
              ],
              [
                "Unverified claims",
                "All decisions are based on documented evidence",
              ],
            ].map(([title, body]) => (
              <li key={title} className="pl-1">
                <span className="font-bold text-[#002726]">{title}</span> -{" "}
                <span className="font-normal text-[#002726]">{body}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 8 */}
        <section
          id="section-8"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>How to Request a Refund</h2>
          <p className={p}>
            If you believe you meet the eligibility criteria above, here&apos;s
            how to submit your request.
          </p>
          <ol className="list-decimal space-y-6 pl-6 marker:text-[#002726]">
            <li className="pl-2">
              <span className="font-semibold text-[#002726]">Email Us</span>
              <p className={p}>
                Send an email to{" "}
                <a href="mailto:hi@growthschool.io" className={link}>
                  hi@growthschool.io
                </a>{" "}
                with the subject line:{" "}
                <span className="font-semibold text-[#002726]">
                  Refund Request - [Your Full Name] - [Course Name]
                </span>
              </p>
            </li>
            <li className="pl-2">
              <span className="font-semibold text-[#002726]">
                Include the Following
              </span>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-[#002726] md:text-base marker:text-[#002726]">
                <li>Your registered email address</li>
                <li>Your payment reference or transaction ID</li>
                <li>The name of the course or program</li>
                <li>The date of purchase</li>
                <li>
                  A brief explanation of your reason for requesting a refund
                </li>
                <li>Any supporting documentation where applicable</li>
              </ul>
            </li>
            <li className="pl-2">
              <span className="font-semibold text-[#002726]">
                We&apos;ll Review and Respond
              </span>
              <p className={p}>
                Our team will review your request against the eligibility
                criteria and respond within <strong>3 business days</strong>. We
                may ask for additional information before making a decision.
              </p>
            </li>
            <li className="pl-2">
              <span className="font-semibold text-[#002726]">
                Refund Processing
              </span>
              <p className={p}>
                If approved, we will share a refund summary with the applicable
                deductions for your acknowledgment. Once confirmed, the refund
                will be credited to your original payment method within{" "}
                <strong>7 to 10 business days</strong>.
              </p>
            </li>
          </ol>
        </section>

        {/* Section 9 */}
        <section
          id="section-9"
          className="space-y-6 border-t border-gray-200 pt-10"
        >
          <h2 className={h2}>Getting Further Support</h2>

          <div className="space-y-3">
            <h3 className={h3}>9.1 No Response from Our Team</h3>
            <p className={p}>
              If you&apos;ve submitted a refund request and haven&apos;t
              received a response within 5 business days, please reply to your
              original email with the subject line:{" "}
              <span className="font-semibold text-[#002726]">
                &quot;Follow Up - [Your Name] - [Course Name]&quot;
              </span>
              . Your case will be picked up and treated as a priority.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>9.2 Disagreement With Our Decision</h3>
            <p className={p}>
              If you disagree with the outcome of your refund request, you may
              reply to the decision email with your reasoning and any additional
              supporting documentation. A senior member of our team will review
              the case independently and respond within 5 business days. We want
              to make sure every concern is heard and resolved fairly. If
              you&apos;re still not satisfied after this review, your rights
              under applicable consumer protection law remain fully available to
              you.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className={h3}>9.3 Payment Disputes and Chargebacks</h3>
            <p className={p}>
              We strongly encourage reaching out to our team before initiating a
              chargeback or payment dispute with your bank or credit card
              provider. Most concerns can be resolved quickly and directly. If a
              chargeback is initiated before contacting us, your access to the
              program may be reviewed. We take all disputes seriously and will
              cooperate fully with your payment provider throughout the process.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="space-y-6 border-t border-gray-200 pt-10">
          <h2 className={h2}>Frequently Asked Questions</h2>

          <div className="space-y-2">
            {FAQ_ITEMS.map((item, i) => {
              const id = `faq-${i}`;
              return (
                <div
                  key={id}
                  className="overflow-hidden rounded-lg border border-gray-200"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 bg-white px-4 py-3 text-left text-sm font-medium text-[#002726] hover:bg-gray-50 md:text-base"
                    onClick={() => toggleFaq(id)}
                    aria-expanded={openFaqs.has(id)}
                  >
                    <span>{item.q}</span>
                    <span className="shrink-0 text-[#686C7A]">
                      {openFaqs.has(id) ? "−" : "+"}
                    </span>
                  </button>
                  {openFaqs.has(id) && (
                    <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                      <FaqAnswer text={item.a} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Final */}
        <section className="space-y-4 text-left">
          <h2 className="text-2xl font-medium text-[#002726]">
            Still Have Questions?
          </h2>
          <p className={p}>
            We put our best into every program we build. If you&apos;re on the
            fence about enrolling, we&apos;d rather you reach out and ask
            questions before purchasing than feel uncertain after. Our team is
            happy to help you decide if a program is the right fit.
          </p>
          <p className={p}>
            <a href="mailto:hi@growthschool.io" className={link}>
              hi@growthschool.io
            </a>{" "}
            - Pre-purchase questions
          </p>
          <p className={p}>
            <a href="mailto:hi@growthschool.io" className={link}>
              hi@growthschool.io
            </a>{" "}
            - Refund requests
          </p>
        </section>

        <footer className="border-t border-gray-200 pt-8 text-xs text-[#686C7A] leading-relaxed">
          GrowthSchool reserves the right to amend this Refund Policy at any time.
          Updates will be published on this page with a revised date. Continued
          use of GrowthSchool&apos;s services after any update constitutes
          acceptance of the revised policy. In case of any conflict between this
          policy and any other communication, this policy shall prevail.
          <br />
          <br />
          This policy is governed by the laws of India. Any disputes are subject
          to the exclusive jurisdiction of courts in Bengaluru, Karnataka.
          <br />
          <br />
          {LAST_UPDATED_TEXT}
        </footer>
      </div>
    </article>
  );
}
